import { useEffect, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import AgoraRTM from "agora-rtm-sdk";
import { Row, Col } from "antd";
import moment from "moment";

import { Modal, Text, Button } from "../../components";
import { Colors, Fonts } from "../../config";
import { MontserratBold } from "../../config/fonts";
import { FaClock, FaPause, FaPlay } from "react-icons/fa";
import { MdGroups } from "react-icons/md";
import { useDispatch } from "react-redux";
import { LiveAction } from "../../store/actions";

const sendEndStreamMessage = async ({
  channelName,
  rtmToken,
  agoraAppId,
  uid = null,
}) => {
  let rtmClient = null;
  let channel = null;

  try {
    console.log("[RTM] Starting end stream message with params:", {
      channelName,
      uid,
      rtmToken,
      agoraAppId: agoraAppId.substring(0, 8) + "...",
    });

    if (!channelName || !rtmToken || !agoraAppId) {
      return {
        success: false,
        error:
          "Missing required parameters: channelName, rtmToken, or agoraAppId",
      };
    }

    // Create RTM client
    rtmClient = AgoraRTM.createInstance(agoraAppId);

    console.log("[RTM] RTM client created");

    // Login with RTM token - uid MUST match the uid used to generate the token
    await rtmClient.login({
      uid: uid,
      token: rtmToken,
    });

    console.log("[RTM] Successfully logged in with uid:", uid);

    // Join RTM channel
    channel = rtmClient.createChannel(channelName);
    await channel.join();

    console.log("[RTM] Successfully joined channel:", channelName);

    // Send "END_STREAM" message to notify all viewers
    await channel.sendMessage({ text: "End Stream" });

    console.log("[RTM] End stream message sent successfully");

    // Cleanup RTM connection
    await channel.leave();
    await rtmClient.logout();

    console.log("[RTM] Cleanup completed");

    return { success: true };
  } catch (error) {
    console.error("[RTM] Full error object:", error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error("[RTM] Error details:", {
      message: errorMessage,
      name: error instanceof Error ? error.name : "Unknown",
      code: error,
    });

    // Try to cleanup on error
    try {
      if (channel) {
        await channel.leave().catch(() => {});
      }
      if (rtmClient) {
        await rtmClient.logout().catch(() => {});
      }
    } catch (cleanupError) {
      console.error("[RTM] Cleanup error:", cleanupError);
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

const AdminLiveStreamModal = ({
  showModal,
  setShowModal,
  showDetail,
  setShowDetail,
}) => {
  const agoraClientRef = useRef(null);
  const videoContainerRef = useRef(null);
  const remoteVideoTrackRef = useRef(null);
  const remoteAudioTrackRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const hasLiveStream = !!showDetail?.channel && !!showDetail?.token;
  const dispatch = useDispatch();
  const BUCKET = process.env.REACT_APP_MEDIA_BASE_URL;

  useEffect(() => {
    if (!showModal) return;
    if (!showDetail?.channel || !showDetail?.token) return;

    const initAgora = async () => {
      const client = AgoraRTC.createClient({
        mode: "live",
        codec: "vp8",
        role: "host",
      });

      agoraClientRef.current = client;

      // await client.setClientRole("audience");

      await client.join(
        process.env.REACT_APP_AGORA_APP_ID,
        showDetail.channel,
        showDetail.token,
        null,
      );

      client.on("user-published", async (user, mediaType) => {
        await client.subscribe(user, mediaType);

        if (mediaType === "video" && videoContainerRef.current) {
          remoteVideoTrackRef.current = user.videoTrack;
          user.videoTrack.play(videoContainerRef.current, { fit: "contain" });
        }

        if (mediaType === "audio") {
          remoteAudioTrackRef.current = user.audioTrack;
          user.audioTrack.play();
        }
      });

      client.on("user-left", () => {
        console.log("Live stream ended");
      });
    };

    initAgora();

    return () => {
      if (agoraClientRef.current) {
        agoraClientRef.current.removeAllListeners();
        agoraClientRef.current.leave();
        agoraClientRef.current = null;
      }
      remoteVideoTrackRef.current = null;
      remoteAudioTrackRef.current = null;
      setIsPlaying(true);
    };
  }, [showModal, showDetail]);

  const togglePlayPause = () => {
    if (isPlaying) {
      // Pause both video and audio
      if (remoteVideoTrackRef.current) {
        remoteVideoTrackRef.current.stop();
      }
      if (remoteAudioTrackRef.current) {
        remoteAudioTrackRef.current.stop();
      }
      setIsPlaying(false);
    } else {
      // Resume both video and audio
      if (remoteVideoTrackRef.current && videoContainerRef.current) {
        remoteVideoTrackRef.current.play(videoContainerRef.current, {
          fit: "contain",
        });
      }
      if (remoteAudioTrackRef.current) {
        remoteAudioTrackRef.current.play();
      }
      setIsPlaying(true);
    }
  };

  const handleClose = () => {
    setShowDetail(null);
    setShowModal(false);
  };

  const handleCloseEvent = () => {
    dispatch(
      LiveAction.endEvent(showDetail?.id, async () => {
        try {
          console.log("agoraClientRef.current", agoraClientRef.current);
          const channel_Name = showDetail?.channel;
          const rtm_Token = showDetail?.admin_rtm_token;
          const agoraAppId = process.env.REACT_APP_AGORA_APP_ID;
          const uid = "1"; // admin_id
          const result = await sendEndStreamMessage({
            channelName: channel_Name,
            rtmToken: rtm_Token,
            agoraAppId: agoraAppId,
            uid,
          });

          if (result.success) {
            console.log("[v0] End stream message sent successfully via RTM");
          } else {
            console.error("[v0] RTM Error:", result.error);
          }
        } catch (err) {
          console.error("RTM End Stream Error:", err);
        }

        // Refresh live events list
        dispatch(LiveAction.getLiveStreaming({ liveType: "live", page: 1 }));
      }),
    );

    setShowModal(false);
    setShowDetail(null);
  };

  const handleSendNotification = () => {
    dispatch(
      LiveAction.sendEventNotification({
        event_id: showDetail?.id,
      }),
    );
  };

  return (
    <Modal.Basic
      className="modal-lg"
      centered={true}
      title=""
      open={showModal}
      handleCancel={handleClose}
    >
      {/* TITLE (UNCHANGED) */}
      <Row className="flex flex-col items-start">
        <Col>
          <Text.Basic
            color={Colors.Black}
            fontSize={22}
            fontWeight={600}
            fontFamily={Fonts[MontserratBold]}
            text={showDetail?.title}
          />
        </Col>
        {/* ACTION BUTTONS (ONLY UI) */}
        <div className="mt-6 flex justify-end gap-3 my-6">
          <Button.Basic
            text="CCOR Non-Conformity Notice"
            size="small"
            buttonStyle={{
              backgroundColor: !hasLiveStream ? "#D1D5DB" : Colors.BlueText,
              color: Colors.White,
              borderRadius: "6px",
              padding: "24px 16px",
            }}
            disabled={!hasLiveStream}
            onClick={handleSendNotification}
          />

          <Button.Basic
            text="Close Live Stream"
            size="small"
            buttonStyle={{
              backgroundColor: !hasLiveStream ? "#D1D5DB" : Colors.Pink,
              color: Colors.White,
              borderRadius: "6px",
              padding: "24px 16px",
            }}
            disabled={!hasLiveStream}
            onClick={handleCloseEvent}
          />
        </div>
      </Row>

      {/* LIVE VIDEO AREA (SAME POSITION & STYLE) */}
      <div className="bg-gray-200 rounded-3xl mb-4 pb-4">
        <Row justify="center">
          <Col span={24} className="flex-row-align-center">
            <div className="w-full h-auto">
              <div className="relative w-full bg-black rounded-lg group">
                {hasLiveStream ? (
                  <div
                    ref={videoContainerRef}
                    style={{
                      width: "100%",
                      height: "300px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  />
                ) : (
                  <div
                    className="w-full h-[300px] flex justify-center items-center rounded-3xl"
                    style={{
                      backgroundImage: `url(${
                        process.env.REACT_APP_MEDIA_BASE_URL +
                        showDetail?.media[0].path
                      })`,
                    }}
                  >
                    <Text.Basic
                      color={Colors.White}
                      fontSize={22}
                      fontWeight={600}
                      fontFamily={Fonts[MontserratBold]}
                      text="Live not started yet"
                    />
                  </div>
                )}

                {/* Play/Pause Control Button */}
                {hasLiveStream && (
                  <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] z-10 hidden group-hover:block">
                    <button
                      onClick={togglePlayPause}
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                        border: "2px solid white",
                        cursor: "pointer",
                        color: "white",
                      }}
                    >
                      {isPlaying ? <FaPause size={20} /> : <FaPlay size={20} />}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* DETAILS  */}
        <div className="mt-4 flex justify-between px-12 items-center">
          <p className="font-bold text-2xl">{showDetail?.organizer?.name}</p>

          <div className="flex items-center gap-2 text-md">
            <FaClock className="text-yellow-600" size={20} />
            <p>
              {showDetail?.started_at
                ? "Started at " +
                  moment
                    .utc(showDetail?.started_at) // treat backend time as UTC
                    .local() // convert to user’s local time
                    .format("hh:mm A")
                : "Not started yet"}
            </p>
          </div>
        </div>

        <div className="mt-4 flex justify-between px-12 text-md items-center">
          <p>
            {showDetail?.started_at
              ? moment
                  .utc(showDetail?.started_at)
                  .local()
                  .format("DD MMM, YYYY")
              : "--/--/--"}
          </p>

          <div className="flex items-center gap-2">
            <MdGroups color={Colors.BlueText} size={20} />
            <p>{showDetail?.total_viewers || 0 + " People Attending"}</p>
          </div>
        </div>
      </div>
    </Modal.Basic>
  );
};

export default AdminLiveStreamModal;
